import React, { useState, Fragment, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDynamicPermissions } from '../../hooks/useDynamicPermissions';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useIsServiceDeskUser } from '../../hooks/useIsServiceDeskUser';
import { trackChangeOperation } from '../../utils/appInsights';
import { ArrowLeftIcon, ChevronUpDownIcon, CheckIcon } from '@heroicons/react/24/outline';
import RichTextEditor from '../../components/RichTextEditor';
import { Combobox, Transition } from '@headlessui/react';
import { datetimeLocalToUTC } from '../../utils/dateHelpers';
import DateTimePickerInput from '../../components/DateTimePickerInput';
import { useDateFormatter } from '../../hooks/useDateFormatter';
import { useEngagementResources } from '../../hooks/useEngagementResources';
import { fetchAssignmentGroupMembers } from '../../utils/groupMembers';

interface FormData {
  shortDescription: string;
  description: string;
  plannedStart: string;
  plannedEnd: string;
  implementationPlan: string;
  backoutPlan: string;
  testPlan: string;
  justification: string;
  riskImpactAnalysis: string;
  changeType: string;
  risk: string;
  impact: string;
  priority: string;
  cabRequired: boolean;
  assignedTo: string;
  assignmentGroup: string;
  approvalGroup: string;
  customerId: string;
  engagementId: string;
  contactId: string;
  resourceId: string;
  otherResourceNames: string;
}

const impacts = [
  { value: 'high_1', label: '1 - High' },
  { value: 'medium_2', label: '2 - Medium' },
  { value: 'low_3', label: '3 - Low' }
];

// Priority calculation based on Impact × Risk matrix
const calculatePriorityFromImpactRisk = (impact: string, risk: string): string => {
  // Map risk values to numbers
  const riskMap: { [key: string]: number } = {
    'high': 1,
    'moderate': 2,
    'low': 3
  };
  
  const impactNum = parseInt(impact.split('_')[1]) || 3;
  const riskNum = riskMap[risk] || 3;
  
  if (impactNum === 1 && riskNum === 1) return '1'; // Critical
  if ((impactNum === 1 && riskNum === 2) || (impactNum === 2 && riskNum === 1)) return '2'; // High
  if ((impactNum === 1 && riskNum === 3) || (impactNum === 2 && riskNum === 2) || (impactNum === 3 && riskNum === 1)) return '3'; // Moderate
  if ((impactNum === 2 && riskNum === 3) || (impactNum === 3 && riskNum === 2)) return '4'; // Low
  if (impactNum === 3 && riskNum === 3) return '5'; // Planning
  
  return '3'; // Default to Moderate
};

const ChangeCreate: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can, loading: permissionsLoading } = useDynamicPermissions();
  const hasPermission = can('changes', 'create');
  const { preferences } = useDateFormatter();
  const { user } = useAuth();
  const isServiceDeskUser = useIsServiceDeskUser();


  const [formData, setFormData] = useState<FormData>({
    shortDescription: '',
    description: '',
    plannedStart: '',
    plannedEnd: '',
    implementationPlan: '',
    backoutPlan: '',
    testPlan: '',
    justification: '',
    riskImpactAnalysis: '',
    changeType: 'normal',
    risk: 'low',
    impact: 'low_3',
    priority: '3',
    cabRequired: true, // Default to true for 'normal' type
    assignedTo: '',
    assignmentGroup: '',
    approvalGroup: '',
    customerId: '',
    engagementId: '',
    contactId: '',
    resourceId: '',
    otherResourceNames: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [selectedResourceIdValue, setSelectedResourceIdValue] = useState<string>('');
  const [isOtherResource, setIsOtherResource] = useState<boolean>(false);
  const [pinnedResource, setPinnedResource] = useState<{
    id: string;
    name: string;
    resourceId?: string;
    resourceType?: string;
    resourceGroupName?: string;
  } | null>(null);

  // Combobox query states
  const [customerQuery, setCustomerQuery] = useState('');
  const [engagementQuery, setEngagementQuery] = useState('');
  const [resourceQuery, setResourceQuery] = useState('');
  const [contactQuery, setContactQuery] = useState('');
  const [assignmentGroupQuery, setAssignmentGroupQuery] = useState('');
  const [assignedToQuery, setAssignedToQuery] = useState('');
  const [approvalGroupQuery, setApprovalGroupQuery] = useState('');

  // Fetch assignment groups for dropdown
  const { data: assignmentGroupsData } = useQuery({
    queryKey: ['assignment-groups'],
    queryFn: async () => {
      const response = await api.get('/groups/assignment-groups');
      return response.data;
    }
  });

  // Fetch approval groups for dropdown
  const { data: approvalGroupsData } = useQuery({
    queryKey: ['approval-groups'],
    queryFn: async () => {
      const response = await api.get('/groups/approval-groups');
      return response.data;
    }
  });

  // Fetch group members for assignment dropdown - only when assignment group is selected
  const { data: groupMembersData } = useQuery({
    queryKey: ['group-members', formData.assignmentGroup, formData.customerId],
    queryFn: async () => {
      if (!formData.assignmentGroup) return [];
      return fetchAssignmentGroupMembers(formData.assignmentGroup, formData.customerId || null);
    },
    enabled: !!formData.assignmentGroup
  });

  // Fetch customers for dropdown
  const { data: customersData } = useQuery({
    queryKey: ['customers'],
    queryFn: async () => {
      const response = await api.get('/customers');
      return response.data;
    }
  });

  // Fetch engagements for dropdown - only when customer is selected
  const { data: engagementsData } = useQuery({
    queryKey: ['engagements', formData.customerId],
    queryFn: async () => {
      if (!formData.customerId) {
        return { engagements: [] };
      }
      const response = await api.get(`/engagements?customerId=${formData.customerId}`);
      return response.data;
    },
    enabled: !!formData.customerId
  });

  // Fetch resources for dropdown - ServiceNow CIs when engagement is SN-configured
  const { data: engagementResources } = useEngagementResources(
    formData.engagementId,
    resourceQuery,
    pinnedResource
  );
  const resourcesData = engagementResources?.resources || [];
  const isServiceNowResourceSource = engagementResources?.source === 'servicenow';

  // Fetch contacts for Contact dropdown - filtered by customer
  const { data: contactsData } = useQuery({
    queryKey: ['contacts', formData.customerId],
    queryFn: async () => {
      if (!formData.customerId) {
        return { contacts: [] };
      }
      const response = await api.get(`/contacts/dropdown?customerId=${formData.customerId}`);
      return response.data;
    },
    enabled: !!formData.customerId
  });

  // Fetch primary contact for auto-fill when customer/engagement is selected
  const { data: primaryContactData } = useQuery({
    queryKey: ['primary-contact', formData.customerId, formData.engagementId],
    queryFn: async () => {
      if (!formData.customerId) return { contact: null };
      const params = new URLSearchParams({ customerId: formData.customerId });
      if (formData.engagementId) {
        params.append('engagementId', formData.engagementId);
      }
      const response = await api.get(`/contacts/primary?${params.toString()}`);
      return response.data;
    },
    enabled: !!formData.customerId
  });

  // Auto-fill contact when customer/engagement is selected and primary contact is found
  useEffect(() => {
    if (formData.customerId && primaryContactData?.contact && !formData.contactId) {
      setFormData(prev => ({
        ...prev,
        contactId: primaryContactData.contact.id
      }));
    }
  }, [formData.customerId, formData.engagementId, primaryContactData, formData.contactId]);

  const createChangeMutation = useMutation({
    mutationFn: async (data: FormData) => {
      const response = await api.post('/changes', data);
      return response.data;
    },
    onSuccess: async (data) => {
      // Invalidate list queries so they refetch when user navigates back
      await queryClient.invalidateQueries({ queryKey: ['changes'] });
      
      // Log change creation
      if (user) {
        trackChangeOperation(
          'Change Created',
          user.email || user.firstName || 'Unknown',
          {
            action: 'New Change is successfully created',
            changeNumber: data.number || data.id || data._id || 'Unknown',
          },
          {
            firstName: user.firstName,
            lastName: user.lastName,
            role: user.role,
            roleType: user.roleType,
            roleLevel: user.roleLevel,
            userClass: user.class,
            customerId: user.customerId,
            customerName: user.customerName,
            engagementId: user.engagementId,
            engagementName: user.engagementName,
            isServiceDeskUser: isServiceDeskUser,
          }
        );
      }
      
      const newId = data.id || data._id;
      navigate(`/changes/${newId}`);
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to create change request';
      setErrors({ submit: message });
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form - all mandatory fields
    const newErrors: Record<string, string> = {};
    if (!formData.shortDescription.trim()) {
      newErrors.shortDescription = 'Short description is required';
    }
    if (!formData.changeType) {
      newErrors.changeType = 'Change type is required';
    }
    if (!formData.risk) {
      newErrors.risk = 'Risk assessment is required';
    }
    if (!formData.impact) {
      newErrors.impact = 'Impact is required';
    }
    if (!formData.customerId) {
      newErrors.customerId = 'Customer is required';
    }
    if (!formData.engagementId) {
      newErrors.engagementId = 'Engagement is required';
    }
    if (!formData.resourceId && !isOtherResource) {
      newErrors.resourceId = formData.engagementId
        ? 'Resource is required (select a resource or choose "Others")'
        : 'Please select an engagement first';
    }
    if (isOtherResource && !formData.otherResourceNames.trim()) {
      newErrors.otherResourceNames = 'Please enter at least one resource name';
    }
    // Approval Group is required for Normal and Emergency change types
    if ((formData.changeType === 'normal' || formData.changeType === 'emergency') && !formData.approvalGroup) {
      newErrors.approvalGroup = 'Approval Group is required for Normal and Emergency changes';
    }
    if (!formData.justification.trim()) {
      newErrors.justification = 'Justification is required';
    }
    if (!formData.riskImpactAnalysis.trim()) {
      newErrors.riskImpactAnalysis = 'Risk & Impact Analysis is required';
    }
    if (!formData.implementationPlan.trim()) {
      newErrors.implementationPlan = 'Implementation Plan is required';
    }
    if (!formData.backoutPlan.trim()) {
      newErrors.backoutPlan = 'Backout Plan is required';
    }
    if (!formData.testPlan.trim()) {
      newErrors.testPlan = 'Test Plan is required';
    }
    if (!formData.assignmentGroup) {
      newErrors.assignmentGroup = 'Assignment Group is required';
    }
    if (!formData.plannedStart) {
      newErrors.plannedStart = 'Planned start date is required';
    }
    if (!formData.plannedEnd) {
      newErrors.plannedEnd = 'Planned end date is required';
    }

    // Date validation
    if (formData.plannedStart && formData.plannedEnd) {
      const startDate = new Date(formData.plannedStart);
      const endDate = new Date(formData.plannedEnd);
      if (endDate <= startDate) {
        newErrors.plannedEnd = 'Planned end date must be after planned start date';
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    // Calculate priority based on Impact × Risk
    const calculatedPriority = calculatePriorityFromImpactRisk(formData.impact, formData.risk);

    // Submit all fields including planning details
    const submitData: any = {
      shortDescription: formData.shortDescription,
      description: formData.description,
      changeType: formData.changeType,
      risk: formData.risk,
      impact: formData.impact,
      cabRequired: formData.cabRequired,
      priority: calculatedPriority,
      category: 'change',
      urgency: formData.impact, // Map impact to urgency for compatibility
      customerId: formData.customerId,
      engagementId: formData.engagementId,
      assignedToId: formData.assignedTo,
      justification: formData.justification,
      riskImpactAnalysis: formData.riskImpactAnalysis,
      implementationPlan: formData.implementationPlan,
      backoutPlan: formData.backoutPlan,
      testPlan: formData.testPlan,
      plannedStart: datetimeLocalToUTC(formData.plannedStart, preferences.timezone),
      plannedEnd: datetimeLocalToUTC(formData.plannedEnd, preferences.timezone)
    };

    // Add resource - either resourceId OR otherResourceNames (mutually exclusive)
    if (isOtherResource && formData.otherResourceNames.trim()) {
      submitData.otherResourceNames = formData.otherResourceNames.trim();
      submitData.resourceId = null; // Explicitly set to null when using Others
    } else if (formData.resourceId) {
      submitData.resourceId = formData.resourceId;
      submitData.otherResourceNames = null; // Clear otherResourceNames when using resource
    }

    // Add optional fields if provided
    if (formData.contactId) submitData.contactId = formData.contactId;
    if (formData.assignmentGroup) submitData.assignmentGroupId = formData.assignmentGroup;
    if (formData.approvalGroup) submitData.approvalGroupId = formData.approvalGroup;

    createChangeMutation.mutate(submitData);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => {
      const updated = {
        ...prev,
        [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
      };

      // Clear dependent fields when parent fields change
      if (name === 'customerId') {
        updated.engagementId = '';
        updated.contactId = '';
        updated.resourceId = '';
      }
      if (name === 'engagementId') {
        updated.contactId = '';
        updated.resourceId = '';
        updated.otherResourceNames = '';
        setIsOtherResource(false);
        setSelectedResourceIdValue('');
      }
      if (name === 'assignmentGroup') {
        updated.assignedTo = '';
      }

      // CAB Required logic based on change type
      if (name === 'changeType') {
        if (value === 'normal' || value === 'emergency') {
          // For Normal and Emergency: CAB is required (checked and disabled)
          updated.cabRequired = true;
        } else if (value === 'standard') {
          // For Standard: CAB is not required (unchecked and disabled)
          updated.cabRequired = false;
          updated.approvalGroup = ''; // Clear approval group for standard changes
        }
      }

      // Clear approval group when CAB Required is unchecked
      if (name === 'cabRequired' && !(e.target as HTMLInputElement).checked) {
        updated.approvalGroup = '';
      }

      return updated;
    });
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Show loading while checking permissions
  if (permissionsLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Check permission
  if (!hasPermission) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          You don't have permission to create changes.
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-full mx-auto px-8 py-6">
        <div className="mb-6">
          <button
            onClick={() => navigate('/changes')}
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            <ArrowLeftIcon className="mr-2 h-4 w-4" />
            Back to Changes
          </button>
        </div>

        <div className="bg-white shadow-sm rounded-lg border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <h1 className="text-xl font-semibold text-gray-900">Create New Change Request</h1>
          </div>

          <form onSubmit={handleSubmit} className="p-8">
            {errors.submit && (
              <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
                <p className="text-sm text-red-800">{errors.submit}</p>
              </div>
            )}

            {/* Row 1: Number and Change Type */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Number
                    </label>
                  </div>
                  <div className="col-span-9">
                    <input
                      type="text"
                      value=""
                      readOnly
                      className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                    />
                  </div>
                </div>
              </div>
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="changeType" className="block text-sm font-medium text-gray-700 mb-1">
                      Change Type <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <div className="col-span-9">
                    <select
                      id="changeType"
                      name="changeType"
                      value={formData.changeType}
                      onChange={handleChange}
                      className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 ${
                        errors.changeType
                          ? 'border-red-300 focus:ring-red-400'
                          : 'border-gray-300'
                      }`}
                    >
                      <option value="normal">Normal</option>
                      <option value="standard">Standard</option>
                      <option value="emergency">Emergency</option>
                    </select>
                    {errors.changeType && (
                      <p className="mt-1 text-sm text-red-600">{errors.changeType}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Row 2: Risk Assessment and Impact */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="risk" className="block text-sm font-medium text-gray-700 mb-1">
                      Risk Assessment <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <div className="col-span-9">
                    <select
                      id="risk"
                      name="risk"
                      value={formData.risk}
                      onChange={handleChange}
                      className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 ${
                        errors.risk 
                          ? 'border-red-300 focus:ring-red-400' 
                          : 'border-gray-300'
                      }`}
                    >
                      <option value="low">3 - Low</option>
                      <option value="moderate">2 - Moderate</option>
                      <option value="high">1 - High</option>
                    </select>
                    {errors.risk && (
                      <p className="mt-1 text-sm text-red-600">{errors.risk}</p>
                    )}
                  </div>
                </div>
              </div>
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="impact" className="block text-sm font-medium text-gray-700 mb-1">
                      Impact <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <div className="col-span-9">
                    <select
                      id="impact"
                      name="impact"
                      value={formData.impact}
                      onChange={handleChange}
                      className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 ${
                        errors.impact 
                          ? 'border-red-300 focus:ring-red-400' 
                          : 'border-gray-300'
                      }`}
                    >
                      {impacts.map(impact => (
                        <option key={impact.value} value={impact.value}>{impact.label}</option>
                      ))}
                    </select>
                    {errors.impact && (
                      <p className="mt-1 text-sm text-red-600">{errors.impact}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Row 3: Priority (readonly) */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Priority
                    </label>
                  </div>
                  <div className="col-span-9">
                    <input
                      type="text"
                      value={(() => {
                        const priority = calculatePriorityFromImpactRisk(formData.impact, formData.risk);
                        switch (priority) {
                          case '1': return '1 - Critical';
                          case '2': return '2 - High';
                          case '3': return '3 - Medium';
                          case '4': return '4 - Low';
                          case '5': return '5 - Planning';
                          default: return '3 - Medium';
                        }
                      })()}
                      disabled
                      readOnly
                      className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Row 4: Customer and Engagement */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="customerId" className="block text-sm font-medium text-gray-700 mb-1">
                      Customer <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <div className="col-span-9">
                    <Combobox
                      value={formData.customerId || ''}
                      onChange={(value: string) => {
                        setFormData(prev => ({
                          ...prev,
                          customerId: value || '',
                          engagementId: '',
                          contactId: '',
                          resourceId: '',
                          otherResourceNames: ''
                        }));
                        setIsOtherResource(false);
                        setPinnedResource(null);
                        setSelectedResourceIdValue('');
                        if (errors.customerId) {
                          setErrors(prev => ({ ...prev, customerId: '' }));
                        }
                      }}
                    >
                      <div className="relative">
                        <Combobox.Input
                          className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 ${errors.customerId ? 'border-red-300' : 'border-gray-300'}`}
                          displayValue={(customerId: string) => {
                            const customer = (customersData?.customers || []).find((c: any) => c.id === customerId);
                            return customer ? customer.accountName : '';
                          }}
                          onChange={(e) => setCustomerQuery(e.target.value)}
                          placeholder="Select Customer"
                        />
                        <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                          <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                        </Combobox.Button>
                        <Transition
                          as={Fragment}
                          leave="transition ease-in duration-100"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                          afterLeave={() => setCustomerQuery('')}
                        >
                          <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                            {(customersData?.customers || [])
                              .filter((customer: any) => customer.accountName.toLowerCase().includes(customerQuery.toLowerCase()))
                              .map((customer: any) => (
                              <Combobox.Option
                                key={customer.id}
                                value={customer.id}
                                className={({ active }) =>
                                  `relative cursor-pointer select-none py-2 pl-3 pr-9 ${active ? 'bg-indigo-600 text-white' : 'text-gray-900'}`
                                }
                              >
                                {({ selected, active }) => (
                                  <>
                                    <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                      {customer.accountName}
                                    </span>
                                    {selected && (
                                      <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                        <CheckIcon className="h-4 w-4" />
                                      </span>
                                    )}
                                  </>
                                )}
                              </Combobox.Option>
                            ))}
                          </Combobox.Options>
                        </Transition>
                      </div>
                    </Combobox>
                    {errors.customerId && (
                      <p className="mt-1 text-sm text-red-600">{errors.customerId}</p>
                    )}
                  </div>
                </div>
              </div>
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="engagementId" className="block text-sm font-medium text-gray-700 mb-1">
                      Engagement <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <div className="col-span-9">
                    <Combobox
                      value={formData.engagementId || ''}
                      onChange={(value: string) => {
                        setFormData(prev => ({
                          ...prev,
                          engagementId: value || '',
                          contactId: '',
                          resourceId: ''
                        }));
                        setIsOtherResource(false);
                        setPinnedResource(null);
                        setSelectedResourceIdValue('');
                        if (errors.engagementId) {
                          setErrors(prev => ({ ...prev, engagementId: '' }));
                        }
                      }}
                      disabled={!formData.customerId}
                    >
                      <div className="relative">
                        <Combobox.Input
                          className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 ${!formData.customerId ? 'bg-gray-50 text-gray-400' : ''} ${errors.engagementId ? 'border-red-300' : 'border-gray-300'}`}
                          displayValue={(engagementId: string) => {
                            const engagement = (engagementsData?.engagements || []).find((e: any) => e.id === engagementId);
                            return engagement ? `${engagement.name} - ${engagement.status}` : '';
                          }}
                          onChange={(e) => setEngagementQuery(e.target.value)}
                          placeholder="Select Engagement"
                        />
                        <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                          <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                        </Combobox.Button>
                        <Transition
                          as={Fragment}
                          leave="transition ease-in duration-100"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                          afterLeave={() => setEngagementQuery('')}
                        >
                          <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                            {(engagementsData?.engagements || [])
                              .filter((engagement: any) => `${engagement.name} ${engagement.status}`.toLowerCase().includes(engagementQuery.toLowerCase()))
                              .map((engagement: any) => (
                              <Combobox.Option
                                key={engagement.id}
                                value={engagement.id}
                                className={({ active }) =>
                                  `relative cursor-pointer select-none py-2 pl-3 pr-9 ${active ? 'bg-indigo-600 text-white' : 'text-gray-900'}`
                                }
                              >
                                {({ selected, active }) => (
                                  <>
                                    <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                      {engagement.name} - {engagement.status}
                                    </span>
                                    {selected && (
                                      <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                        <CheckIcon className="h-4 w-4" />
                                      </span>
                                    )}
                                  </>
                                )}
                              </Combobox.Option>
                            ))}
                          </Combobox.Options>
                        </Transition>
                      </div>
                    </Combobox>
                    {errors.engagementId && (
                      <p className="mt-1 text-sm text-red-600">{errors.engagementId}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Row 5: Contact */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="contactId" className="block text-sm font-medium text-gray-700 mb-1">
                      Contact
                    </label>
                  </div>
                  <div className="col-span-9">
                    <Combobox
                      value={formData.contactId || ''}
                      onChange={(value: string) => {
                        setFormData(prev => ({ ...prev, contactId: value || '' }));
                        if (errors.contactId) {
                          setErrors(prev => ({ ...prev, contactId: '' }));
                        }
                      }}
                      disabled={!formData.customerId}
                    >
                      <div className="relative">
                        <Combobox.Input
                          className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 ${!formData.customerId ? 'bg-gray-50 text-gray-400' : ''} ${errors.contactId ? 'border-red-300' : 'border-gray-300'}`}
                          displayValue={(contactId: string) => {
                            const contact = (contactsData?.contacts || []).find((c: any) => c.id === contactId);
                            return contact ? contact.name : '';
                          }}
                          onChange={(e) => setContactQuery(e.target.value)}
                          placeholder={!formData.customerId ? 'Select Customer first' : 'Select Contact'}
                        />
                        <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                          <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                        </Combobox.Button>
                        <Transition
                          as={Fragment}
                          leave="transition ease-in duration-100"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                          afterLeave={() => setContactQuery('')}
                        >
                          <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                            {(contactsData?.contacts || [])
                              .filter((contact: any) => `${contact.name} ${contact.email}`.toLowerCase().includes(contactQuery.toLowerCase()))
                              .map((contact: any) => (
                              <Combobox.Option
                                key={contact.id}
                                value={contact.id}
                                className={({ active }) =>
                                  `relative cursor-pointer select-none py-2 pl-3 pr-9 ${active ? 'bg-indigo-600 text-white' : 'text-gray-900'}`
                                }
                              >
                                {({ selected, active }) => (
                                  <>
                                    <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                      {contact.name}
                                    </span>
                                    {selected && (
                                      <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                        <CheckIcon className="h-4 w-4" />
                                      </span>
                                    )}
                                  </>
                                )}
                              </Combobox.Option>
                            ))}
                          </Combobox.Options>
                        </Transition>
                      </div>
                    </Combobox>
                    {errors.contactId && <p className="mt-1 text-xs text-red-500">{errors.contactId}</p>}
                  </div>
                </div>
              </div>
            </div>

            {/* Row 6: Resource and Resource ID / Custom Resources */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="resourceId" className="block text-sm font-medium text-gray-700 mb-1">
                      Resource <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <div className="col-span-9">
                    <Combobox
                      value={isOtherResource ? '__OTHER__' : (formData.resourceId || '')}
                      onChange={(value: string) => {
                        if (value === '__OTHER__') {
                          setIsOtherResource(true);
                          setPinnedResource(null);
                          setFormData(prev => ({ ...prev, resourceId: '' }));
                          setSelectedResourceIdValue('');
                        } else {
                          setIsOtherResource(false);
                          const selectedResource = (resourcesData || []).find((r: any) => r.id === value);
                          setPinnedResource(
                            selectedResource
                              ? {
                                  id: selectedResource.id,
                                  name: selectedResource.name,
                                  resourceId: selectedResource.resourceId,
                                  resourceType: selectedResource.resourceType,
                                  resourceGroupName: selectedResource.resourceGroupName,
                                }
                              : null
                          );
                          setSelectedResourceIdValue(selectedResource?.resourceId || '');
                          setFormData(prev => ({ ...prev, resourceId: value || '', otherResourceNames: '' }));
                        }
                        if (errors.resourceId) {
                          setErrors(prev => ({ ...prev, resourceId: '' }));
                        }
                        if (errors.otherResourceNames) {
                          setErrors(prev => ({ ...prev, otherResourceNames: '' }));
                        }
                      }}
                      disabled={!formData.engagementId}
                    >
                      <div className="relative">
                        <Combobox.Input
                          className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 ${!formData.engagementId ? 'bg-gray-50 text-gray-400' : ''} ${errors.resourceId ? 'border-red-300' : 'border-gray-300'}`}
                          displayValue={(resourceId: string) => {
                            if (isOtherResource) return 'Others (specify custom resources)';
                            const resource = (resourcesData || []).find((r: any) => r.id === resourceId) || pinnedResource;
                            if (!resource) return '';
                            return resource.resourceType && resource.resourceGroupName
                              ? `${resource.name} (${resource.resourceType}) - ${resource.resourceGroupName}`
                              : resource.name;
                          }}
                          onChange={(e) => setResourceQuery(e.target.value)}
                          placeholder="Select Resource"
                        />
                        <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                          <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                        </Combobox.Button>
                        <Transition
                          as={Fragment}
                          leave="transition ease-in duration-100"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                          afterLeave={() => setResourceQuery('')}
                        >
                          <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                            {(resourcesData || [])
                              .filter((resource: any) => {
                                if (isServiceNowResourceSource) return true;
                                return `${resource.name} ${resource.resourceType} ${resource.resourceGroupName}`
                                  .toLowerCase()
                                  .includes(resourceQuery.toLowerCase());
                              })
                              .map((resource: any) => (
                              <Combobox.Option
                                key={resource.id}
                                value={resource.id}
                                className={({ active }) =>
                                  `relative cursor-pointer select-none py-2 pl-3 pr-9 ${active ? 'bg-indigo-600 text-white' : 'text-gray-900'}`
                                }
                              >
                                {({ selected, active }) => (
                                  <>
                                    <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                      {resource.name} ({resource.resourceType}) - {resource.resourceGroupName}
                                    </span>
                                    {selected && (
                                      <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                        <CheckIcon className="h-4 w-4" />
                                      </span>
                                    )}
                                  </>
                                )}
                              </Combobox.Option>
                            ))}
                            {/* Others option */}
                            <Combobox.Option
                              key="__OTHER__"
                              value="__OTHER__"
                              className={({ active }) =>
                                `relative cursor-pointer select-none py-2 pl-3 pr-9 border-t border-gray-200 ${active ? 'bg-indigo-600 text-white' : 'text-gray-900'}`
                              }
                            >
                              {({ selected, active }) => (
                                <>
                                  <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                    Others (specify custom resources)
                                  </span>
                                  {selected && (
                                    <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                      <CheckIcon className="h-4 w-4" />
                                    </span>
                                  )}
                                </>
                              )}
                            </Combobox.Option>
                          </Combobox.Options>
                        </Transition>
                      </div>
                    </Combobox>
                    {errors.resourceId && (
                      <p className="mt-1 text-sm text-red-600">{errors.resourceId}</p>
                    )}
                  </div>
                </div>
              </div>
              <div className="col-span-6">
                <div className={`grid grid-cols-12 gap-4 ${isOtherResource ? 'items-start' : 'items-end'}`}>
                  <div className="col-span-3">
                    <label className={`block text-sm font-medium text-gray-700 ${isOtherResource ? 'mt-1' : 'mb-1'}`}>
                      {isOtherResource ? 'Custom Resources' : 'Resource ID'}
                      {isOtherResource && <span className="text-red-500"> *</span>}
                    </label>
                  </div>
                  <div className="col-span-9">
                    {isOtherResource ? (
                      <>
                        <input
                          type="text"
                          value={formData.otherResourceNames}
                          onChange={(e) => {
                            setFormData(prev => ({ ...prev, otherResourceNames: e.target.value }));
                            if (errors.otherResourceNames) {
                              setErrors(prev => ({ ...prev, otherResourceNames: '' }));
                            }
                          }}
                          placeholder="Enter resource names, separated by commas"
                          className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 ${errors.otherResourceNames ? 'border-red-300' : 'border-gray-300'}`}
                        />
                        {errors.otherResourceNames && (
                          <p className="mt-1 text-sm text-red-600">{errors.otherResourceNames}</p>
                        )}
                        <p className="mt-1 text-xs text-gray-500">Enter multiple resource names separated by commas</p>
                      </>
                    ) : (
                      <input
                        type="text"
                        value={selectedResourceIdValue}
                        readOnly
                        className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                        placeholder="Auto-populated from selected resource"
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Row 7: Planned Start and Planned End */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="plannedStart" className="block text-sm font-medium text-gray-700 mb-1">
                      Planned Start <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <div className="col-span-9">
                    <DateTimePickerInput
                      id="plannedStart"
                      name="plannedStart"
                      value={formData.plannedStart}
                      onChange={(value) => setFormData(prev => ({ ...prev, plannedStart: value }))}
                      error={!!errors.plannedStart}
                      required
                    />
                    {errors.plannedStart && (
                      <p className="mt-1 text-sm text-red-600">{errors.plannedStart}</p>
                    )}
                  </div>
                </div>
              </div>
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="plannedEnd" className="block text-sm font-medium text-gray-700 mb-1">
                      Planned End <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <div className="col-span-9">
                    <DateTimePickerInput
                      id="plannedEnd"
                      name="plannedEnd"
                      value={formData.plannedEnd}
                      onChange={(value) => setFormData(prev => ({ ...prev, plannedEnd: value }))}
                      error={!!errors.plannedEnd}
                      min={formData.plannedStart}
                      required
                    />
                    {errors.plannedEnd && (
                      <p className="mt-1 text-sm text-red-600">{errors.plannedEnd}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Row 8: Assignment Group and Assigned To */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="assignmentGroup" className="block text-sm font-medium text-gray-700 mb-1">
                      Assignment Group <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <div className="col-span-9">
                    <Combobox
                      value={formData.assignmentGroup || ''}
                      onChange={(value: string) => {
                        setFormData(prev => ({
                          ...prev,
                          assignmentGroup: value || '',
                          assignedTo: ''
                        }));
                        if (errors.assignmentGroup) {
                          setErrors(prev => ({ ...prev, assignmentGroup: '' }));
                        }
                      }}
                    >
                      <div className="relative">
                        <Combobox.Input
                          className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 ${
                            errors.assignmentGroup
                              ? 'border-red-300 focus:ring-red-400'
                              : 'border-gray-300'
                          }`}
                          displayValue={(groupId: string) => {
                            const group = (assignmentGroupsData || []).find((g: any) => g.id === groupId);
                            return group ? group.name : '';
                          }}
                          onChange={(e) => setAssignmentGroupQuery(e.target.value)}
                          placeholder="Select Assignment Group"
                        />
                        <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                          <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                        </Combobox.Button>
                        <Transition
                          as={Fragment}
                          leave="transition ease-in duration-100"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                          afterLeave={() => setAssignmentGroupQuery('')}
                        >
                          <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                            {(assignmentGroupsData || [])
                              .filter((group: any) => group && group.id && group.name.toLowerCase().includes(assignmentGroupQuery.toLowerCase()))
                              .map((group: any) => (
                              <Combobox.Option
                                key={group.id}
                                value={group.id}
                                className={({ active }) =>
                                  `relative cursor-pointer select-none py-2 pl-3 pr-9 ${active ? 'bg-indigo-600 text-white' : 'text-gray-900'}`
                                }
                              >
                                {({ selected, active }) => (
                                  <>
                                    <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                      {group.name}
                                    </span>
                                    {selected && (
                                      <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                        <CheckIcon className="h-4 w-4" />
                                      </span>
                                    )}
                                  </>
                                )}
                              </Combobox.Option>
                            ))}
                          </Combobox.Options>
                        </Transition>
                      </div>
                    </Combobox>
                    {errors.assignmentGroup && (
                      <p className="mt-1 text-sm text-red-600">{errors.assignmentGroup}</p>
                    )}
                  </div>
                </div>
              </div>
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="assignedTo" className="block text-sm font-medium text-gray-700 mb-1">
                      Assigned To
                    </label>
                  </div>
                  <div className="col-span-9">
                    <Combobox
                      value={formData.assignedTo || ''}
                      onChange={(value: string) => {
                        setFormData(prev => ({ ...prev, assignedTo: value || '' }));
                      }}
                      disabled={!formData.assignmentGroup}
                    >
                      <div className="relative">
                        <Combobox.Input
                          className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 ${!formData.assignmentGroup ? 'bg-gray-50 text-gray-400' : ''} ${errors.assignedTo ? 'border-red-300' : 'border-gray-300'}`}
                          displayValue={(memberId: string) => {
                            const member = (groupMembersData || []).find((m: any) => m.id === memberId);
                            return member ? `${member.firstName} ${member.lastName}` : '';
                          }}
                          onChange={(e) => setAssignedToQuery(e.target.value)}
                          placeholder="Select Assignee"
                        />
                        <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                          <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                        </Combobox.Button>
                        <Transition
                          as={Fragment}
                          leave="transition ease-in duration-100"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                          afterLeave={() => setAssignedToQuery('')}
                        >
                          <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                            {(groupMembersData || [])
                              .filter((member: any) => member && member.id && `${member.firstName} ${member.lastName}`.toLowerCase().includes(assignedToQuery.toLowerCase()))
                              .map((member: any) => (
                              <Combobox.Option
                                key={member.id}
                                value={member.id}
                                className={({ active }) =>
                                  `relative cursor-pointer select-none py-2 pl-3 pr-9 ${active ? 'bg-indigo-600 text-white' : 'text-gray-900'}`
                                }
                              >
                                {({ selected, active }) => (
                                  <>
                                    <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                      {member.firstName} {member.lastName}
                                    </span>
                                    {selected && (
                                      <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                        <CheckIcon className="h-4 w-4" />
                                      </span>
                                    )}
                                  </>
                                )}
                              </Combobox.Option>
                            ))}
                          </Combobox.Options>
                        </Transition>
                      </div>
                    </Combobox>
                    {errors.assignedTo && (
                      <p className="mt-1 text-sm text-red-600">{errors.assignedTo}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Row 9: CAB Required and Approval Group */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-start">
                  <div className="col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      CAB Required
                    </label>
                  </div>
                  <div className="col-span-9">
                    <div className="flex items-start">
                      <div className="flex items-center h-5">
                        <input
                          id="cabRequired"
                          name="cabRequired"
                          type="checkbox"
                          checked={formData.cabRequired}
                          onChange={handleChange}
                          disabled={true}
                          className="focus:ring-blue-400 h-4 w-4 text-blue-600 border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                        />
                      </div>
                      <div className="ml-3 text-sm">
                        <label htmlFor="cabRequired" className="font-medium text-gray-500">
                          Change Advisory Board approval needed before implementation
                          {formData.changeType === 'standard' && <span className="text-gray-500 italic"> (Not required for Standard changes)</span>}
                          {(formData.changeType === 'normal' || formData.changeType === 'emergency') && <span className="text-gray-500 italic"> (Required for {formData.changeType === 'normal' ? 'Normal' : 'Emergency'} changes)</span>}
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {formData.cabRequired && (
                <div className="col-span-6">
                  <div className="grid grid-cols-12 gap-4 items-end">
                    <div className="col-span-3">
                      <label htmlFor="approvalGroup" className="block text-sm font-medium text-gray-700 mb-1">
                        Approval Group <span className="text-red-500">*</span>
                      </label>
                    </div>
                    <div className="col-span-9">
                      <Combobox
                        value={formData.approvalGroup || ''}
                        onChange={(value: string) => {
                          setFormData(prev => ({ ...prev, approvalGroup: value || '' }));
                          if (errors.approvalGroup) {
                            setErrors(prev => ({ ...prev, approvalGroup: '' }));
                          }
                        }}
                      >
                        <div className="relative">
                          <Combobox.Input
                            className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 ${errors.approvalGroup ? 'border-red-300' : 'border-gray-300'}`}
                            displayValue={(groupId: string) => {
                              const group = (approvalGroupsData || []).find((g: any) => g.id === groupId);
                              return group ? group.name : '';
                            }}
                            onChange={(e) => setApprovalGroupQuery(e.target.value)}
                            placeholder="Select Approval Group"
                          />
                          <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                            <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                          </Combobox.Button>
                          <Transition
                            as={Fragment}
                            leave="transition ease-in duration-100"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                            afterLeave={() => setApprovalGroupQuery('')}
                          >
                            <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                              {(approvalGroupsData || [])
                                .filter((group: any) => group && group.id && group.name.toLowerCase().includes(approvalGroupQuery.toLowerCase()))
                                .map((group: any) => (
                                <Combobox.Option
                                  key={group.id}
                                  value={group.id}
                                  className={({ active }) =>
                                    `relative cursor-pointer select-none py-2 pl-3 pr-9 ${active ? 'bg-indigo-600 text-white' : 'text-gray-900'}`
                                  }
                                >
                                  {({ selected, active }) => (
                                    <>
                                      <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                        {group.name}
                                      </span>
                                      {selected && (
                                        <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                          <CheckIcon className="h-4 w-4" />
                                        </span>
                                      )}
                                    </>
                                  )}
                                </Combobox.Option>
                              ))}
                            </Combobox.Options>
                          </Transition>
                        </div>
                      </Combobox>
                      {errors.approvalGroup && (
                        <p className="mt-1 text-sm text-red-600">{errors.approvalGroup}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Row 10: Short Description */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-12">
                <div className="grid grid-cols-12 gap-4 items-start">
                  <div className="col-span-2">
                    <label htmlFor="shortDescription" className="block text-sm font-medium text-gray-700 mb-1">
                      Short Description <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <div className="col-span-10">
                    <RichTextEditor
                      value={formData.shortDescription}
                      onChange={(value) => setFormData(prev => ({ ...prev, shortDescription: value }))}
                      placeholder="Brief summary of the change"
                    />
                    {errors.shortDescription && (
                      <p className="mt-1 text-sm text-red-600">{errors.shortDescription}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Row 11: Description (optional) */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-12">
                <div className="grid grid-cols-12 gap-4 items-start">
                  <div className="col-span-2">
                    <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                  </div>
                  <div className="col-span-10">
                    <RichTextEditor
                      value={formData.description}
                      onChange={(value) => setFormData(prev => ({ ...prev, description: value }))}
                      placeholder="Detailed description of the change"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Row 12: Justification */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-12">
                <div className="grid grid-cols-12 gap-4 items-start">
                  <div className="col-span-2">
                    <label htmlFor="justification" className="block text-sm font-medium text-gray-700 mb-1">
                      Justification <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <div className="col-span-10">
                    <RichTextEditor
                      value={formData.justification}
                      onChange={(value) => setFormData(prev => ({ ...prev, justification: value }))}
                      placeholder="Business justification and reason for this change"
                    />
                    {errors.justification && (
                      <p className="mt-1 text-sm text-red-600">{errors.justification}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Row 13: Risk & Impact Analysis */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-12">
                <div className="grid grid-cols-12 gap-4 items-start">
                  <div className="col-span-2">
                    <label htmlFor="riskImpactAnalysis" className="block text-sm font-medium text-gray-700 mb-1">
                      Risk & Impact Analysis <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <div className="col-span-10">
                    <RichTextEditor
                      value={formData.riskImpactAnalysis}
                      onChange={(value) => setFormData(prev => ({ ...prev, riskImpactAnalysis: value }))}
                      placeholder="Detailed analysis of risks and impacts associated with this change"
                    />
                    {errors.riskImpactAnalysis && (
                      <p className="mt-1 text-sm text-red-600">{errors.riskImpactAnalysis}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Row 14: Implementation Plan */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-12">
                <div className="grid grid-cols-12 gap-4 items-start">
                  <div className="col-span-2">
                    <label htmlFor="implementationPlan" className="block text-sm font-medium text-gray-700 mb-1">
                      Implementation Plan <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <div className="col-span-10">
                    <RichTextEditor
                      value={formData.implementationPlan}
                      onChange={(value) => setFormData(prev => ({ ...prev, implementationPlan: value }))}
                      placeholder="Step-by-step plan for implementing the change"
                    />
                    {errors.implementationPlan && (
                      <p className="mt-1 text-sm text-red-600">{errors.implementationPlan}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Row 15: Backout Plan */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-12">
                <div className="grid grid-cols-12 gap-4 items-start">
                  <div className="col-span-2">
                    <label htmlFor="backoutPlan" className="block text-sm font-medium text-gray-700 mb-1">
                      Backout Plan <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <div className="col-span-10">
                    <RichTextEditor
                      value={formData.backoutPlan}
                      onChange={(value) => setFormData(prev => ({ ...prev, backoutPlan: value }))}
                      placeholder="Steps to revert the change if issues occur"
                    />
                    {errors.backoutPlan && (
                      <p className="mt-1 text-sm text-red-600">{errors.backoutPlan}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Row 16: Test Plan */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-12">
                <div className="grid grid-cols-12 gap-4 items-start">
                  <div className="col-span-2">
                    <label htmlFor="testPlan" className="block text-sm font-medium text-gray-700 mb-1">
                      Test Plan <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <div className="col-span-10">
                    <RichTextEditor
                      value={formData.testPlan}
                      onChange={(value) => setFormData(prev => ({ ...prev, testPlan: value }))}
                      placeholder="How the change will be tested and validated"
                    />
                    {errors.testPlan && (
                      <p className="mt-1 text-sm text-red-600">{errors.testPlan}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => navigate('/changes')}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={createChangeMutation.isPending}
                className="inline-flex justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
              >
                {createChangeMutation.isPending ? 'Creating...' : 'Create Change Request'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ChangeCreate;
